// Session watchdog: read live context fill, and when it crosses a threshold
// emit a scaffold-grounded condense brief. Builds on session.ts / events.ts —
// it does NOT reimplement context injection. The delta is the live threshold
// + the brief.

import type { CaiConfig, WatchdogSettings } from "../types.js";
import { readContextSignal, type ContextSignal } from "./signal.js";
import { buildCondenseBrief, renderBrief, type CondenseBrief } from "./brief.js";

const DEFAULT_SETTINGS: WatchdogSettings = {
  warnPercent: 55,
  criticalPercent: 75,
};

export type WatchdogLevel = "ok" | "warn" | "critical";

export interface WatchdogResult {
  signal: ContextSignal;
  level: WatchdogLevel;
  /** Brief is present only when level is warn or critical. */
  brief: CondenseBrief | null;
}

export interface WatchdogOpts {
  bridgePath?: string;
  transcriptPath?: string;
  windowTokens?: number;
}

export function checkWatchdog(config: CaiConfig, opts: WatchdogOpts = {}): WatchdogResult {
  const settings = resolveSettings(config.settings.watchdog);
  const signal = readContextSignal(opts);

  let level: WatchdogLevel = "ok";
  if (signal.percent >= settings.criticalPercent) level = "critical";
  else if (signal.percent >= settings.warnPercent) level = "warn";

  const brief = level === "ok" ? null : buildCondenseBrief(config);
  return { signal, level, brief };
}

export function runWatchdog(
  config: CaiConfig,
  opts: WatchdogOpts & { json?: boolean } = {}
): WatchdogResult {
  const result = checkWatchdog(config, opts);
  if (opts.json) {
    console.log(JSON.stringify(serialize(result), null, 2));
  } else {
    printWatchdog(result);
  }
  return result;
}

export function serializeWatchdog(result: WatchdogResult) {
  return {
    level: result.level,
    percent: result.signal.percent,
    estimate: result.signal.estimate,
    source: result.signal.source,
    brief: result.brief,
  };
}
const serialize = serializeWatchdog;

function printWatchdog(result: WatchdogResult): void {
  const { signal, level, brief } = result;
  const est = signal.estimate ? " (estimate)" : "";

  if (level === "ok") {
    console.log(`Context fill: ${signal.percent}%${est} — ok`);
    return;
  }

  const label = level === "critical" ? "CRITICAL" : "WARNING";
  console.log(`${label}: context fill ${signal.percent}%${est}`);
  console.log("Condense now — keep what matters, drop the rest:\n");
  if (brief) console.log(renderBrief(brief));
}

function resolveSettings(partial: Partial<WatchdogSettings> | undefined): WatchdogSettings {
  return {
    warnPercent: partial?.warnPercent ?? DEFAULT_SETTINGS.warnPercent,
    criticalPercent: partial?.criticalPercent ?? DEFAULT_SETTINGS.criticalPercent,
  };
}
